import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import PtLogo from '@/components/PtLogo';
import { useTranslation, LanguagePicker } from '@patienttrac/i18n';

// ============================================================================
// Login route — Standalone, two-step
// ----------------------------------------------------------------------------
// Step 1: email + password → signInWithPassword
//         → if invalid, show error and stay on step 1
//         → if valid, fetch MFA status:
//              · MFA enabled → advance to step 2 (code entry)
//              · MFA not enrolled → sign out and redirect to /setup-mfa
// Step 2: 6-digit code → verify_totp
//         → if valid, mark session as MFA-verified and navigate to /
//         → if invalid, show error and stay on step 2
//
// We deliberately do NOT show the code field upfront. Showing it only after
// password verification matches industry-standard 2FA UX and avoids confusing
// first-time users who haven't enrolled yet.
// ============================================================================

type Stage = 'credentials' | 'mfa';

export default function LoginRoute() {
    const navigate = useNavigate();
    const { refreshMfa } = useAuth();
    const { t } = useTranslation();
    const [stage, setStage] = useState<Stage>('credentials');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [code, setCode] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    async function handleCredentials(e: FormEvent) {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            // Step 1: email + password
            const { error: signInErr } = await supabase.auth.signInWithPassword({
                email,
                password
            });
            if (signInErr) {
                setError(t('or.login.err.invalid'));
                return;
            }

            // Check MFA status
            const { data: mfaStatus, error: mfaStatusErr } = await supabase.rpc('get_mfa_status');
            if (mfaStatusErr) {
                setError(t('or.login.err.verify_account'));
                await supabase.auth.signOut();
                return;
            }

            const status = mfaStatus as {
                enabled: boolean;
                has_secret: boolean;
                member_found: boolean;
            };

            if (!status.member_found) {
                setError(t('or.login.err.not_registered'));
                await supabase.auth.signOut();
                return;
            }

            if (status.enabled && status.has_secret) {
                // Advance to MFA prompt
                setStage('mfa');
                setLoading(false);
                return;
            }

            // No MFA enrolled → must enroll before using OR
            sessionStorage.setItem('pt-or:enrollment-in-progress', 'true');
            navigate('/setup-mfa', { replace: true });
        } catch (err) {
            setError(err instanceof Error ? err.message : t('or.login.err.signin_failed'));
            await supabase.auth.signOut();
        } finally {
            setLoading(false);
        }
    }

    async function handleMfa(e: FormEvent) {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            // verify_totp_session verifies the code AND records a server-side
            // MFA-verified marker for this session (saas.mfa_verified_sessions).
            const { data: ok, error: verifyErr } = await supabase.rpc('verify_totp_session', {
                p_code: code
            });

            if (verifyErr) {
                setError(t('or.login.err.verify_failed'));
                return;
            }
            if (!ok) {
                setError(t('or.login.err.invalid_code'));
                return;
            }

            // Sync the server-derived MFA flag before entering the app.
            await refreshMfa();
            navigate('/', { replace: true });
        } catch (err) {
            setError(err instanceof Error ? err.message : t('or.login.err.verify_failed_short'));
        } finally {
            setLoading(false);
        }
    }

    async function handleBack() {
        await supabase.auth.signOut();
        localStorage.removeItem('pt-or:mfa-verified');
        setStage('credentials');
        setCode('');
        setError(null);
    }

    return (
        <div className="min-h-screen flex items-center justify-center px-4">
            <div className="w-full max-w-md">
                {/* Branding */}
                <div className="mb-8 text-center">
                    <div className="inline-flex items-center justify-center mb-4">
                        <PtLogo size={64} />
                    </div>
                    <h1 className="text-2xl font-display font-semibold tracking-wide">
                        PatientTrac <span className="text-pt-gold">Ambulatory</span>
                    </h1>
                    <p className="text-pt-text-muted text-sm mt-1">
                        {t('or.brand.tagline')}
                    </p>
                    <div className="mt-3 flex justify-center"><LanguagePicker className="pt-lang" /></div>
                </div>

                {/* Step 1: credentials */}
                {stage === 'credentials' && (
                    <div className="bg-pt-panel border border-pt-border rounded-lg p-6">
                        <form onSubmit={handleCredentials} className="space-y-4">
                            <h2 className="text-sm font-semibold uppercase tracking-wide text-pt-text-muted">
                                {t('or.login.signin')}
                            </h2>

                            <div>
                                <label className="block text-xs uppercase tracking-wide text-pt-text-muted mb-1.5">
                                    {t('or.login.email')}
                                </label>
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full px-3 py-2.5 bg-pt-dark border border-pt-input-border rounded-md text-pt-text"
                                    autoComplete="email"
                                    autoFocus
                                />
                            </div>

                            <div>
                                <label className="block text-xs uppercase tracking-wide text-pt-text-muted mb-1.5">
                                    {t('or.login.password')}
                                </label>
                                <input
                                    type="password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full px-3 py-2.5 bg-pt-dark border border-pt-input-border rounded-md text-pt-text"
                                    autoComplete="current-password"
                                />
                            </div>

                            {error && (
                                <div className="text-pt-red text-sm py-2 px-3 bg-pt-red/10 border-l-2 border-pt-red rounded">
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-2.5 bg-pt-gold text-pt-darker font-semibold uppercase tracking-wide rounded-md shadow-pt-glow-gold disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-pt-glow-gold-strong transition-shadow"
                            >
                                {loading ? <span className="pt-spinner inline-block" /> : t('or.login.continue')}
                            </button>

                            <div className="text-right">
                                <a
                                    href="https://app.patienttrac.com/?reset=1"
                                    className="text-xs text-pt-text-muted hover:text-pt-text"
                                >
                                    {t('or.login.forgot')}
                                </a>
                            </div>
                        </form>
                    </div>
                )}

                {/* Step 2: MFA verification */}
                {stage === 'mfa' && (
                    <div className="bg-pt-panel border border-pt-border rounded-lg p-6">
                        <form onSubmit={handleMfa} className="space-y-4">
                            <h2 className="text-sm font-semibold uppercase tracking-wide text-pt-text-muted">
                                {t('or.login.mfa_title')}
                            </h2>
                            <p className="text-sm text-pt-text-muted">
                                {t('or.login.mfa_hint_prefix')}
                                <span className="text-pt-text font-medium">{email}</span>.
                            </p>

                            <div>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]{6}"
                                    maxLength={6}
                                    required
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                                    className="w-full px-3 py-3 bg-pt-dark border border-pt-input-border rounded-md text-pt-text text-center font-mono text-xl tracking-widest"
                                    autoFocus
                                    autoComplete="one-time-code"
                                    placeholder="000000"
                                />
                            </div>

                            {error && (
                                <div className="text-pt-red text-sm py-2 px-3 bg-pt-red/10 border-l-2 border-pt-red rounded">
                                    {error}
                                </div>
                            )}

                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={handleBack}
                                    className="px-4 py-2.5 border border-pt-input-border rounded-md text-pt-text-muted hover:border-pt-gold hover:text-pt-text"
                                >
                                    {t('or.login.back')}
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading || code.length !== 6}
                                    className="flex-1 py-2.5 bg-pt-gold text-pt-darker font-semibold uppercase tracking-wide rounded-md shadow-pt-glow-gold disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-pt-glow-gold-strong transition-shadow"
                                >
                                    {loading ? <span className="pt-spinner inline-block" /> : t('or.login.verify')}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                <p className="mt-6 text-center text-xs text-pt-text-dim">
                    {t('or.login.footer')}
                </p>
            </div>
        </div>
    );
}
